"use client";
import { autoFuseNeedsReview } from "@/lib/terminal-fuse-limit";
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeToolbar,
  Position,
  useReactFlow,
  type EdgeProps,
} from "@xyflow/react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { getDefinition } from "@/lib/catalog";
import { useT } from "@/lib/i18n";
import {
  type CableSpec,
  getCableFuse,
  hasHighVDrop,
  isOverloaded,
} from "@/lib/cable-current";
import { currentNeedsVerification, sustainedCableCurrent } from "@/lib/cable-current-evidence";
import { getEffectiveStateOption } from "@/lib/simulate/states";
import { resolveCableColor } from "@/lib/cable-color";
import { acCableSupplyStatus, computeAcAvailability } from "@/lib/ac-live";
import { cableDirection } from "@/lib/cable-direction";
import type { SystemNode } from "@/lib/types";
import {
  findOwnParallelSnap,
  roundedPath,
  segmentOrientation,
  simplifyOrthogonal,
} from "@/lib/cable-routing";
import {
  lerpPolylines,
  polylinePath,
  resamplePolyline,
  samePolyline,
} from "@/lib/cable-anim";
import { useCanvasDrag } from "@/lib/canvas-drag";
import type { RoutingScene } from "@/lib/cable-router/types";
import { useCableRouter } from "@/components/hooks/use-cable-router";
import type { Cable } from "@/lib/types";
import { useSystemStore } from "@/lib/store";
import { useCanvasViewOnly, useCanvasConnectionOverview, useCanvasHideCableLabels } from "./canvas-mode";
import { isConnectionOverviewCable } from "@/lib/connection-overview";
import { useCanvasHover } from "@/lib/canvas-hover";
import { componentFocusCableIds } from "@/lib/component-focus";
import { commentsForCable, useCommentsStore } from "@/lib/comments/store";
import { useCommentPopover } from "@/components/comments/popover-store";
import { isJunction } from "@/lib/node-kind";
import { useFuseVisibility } from "@/lib/settings/fuse-visibility";
import { useCableSizeVisibility } from "@/lib/settings/cable-size-visibility";
import { resolveCableMetricPills } from "@/lib/cable-metric-pills";
import { useCableLengthVisibility } from "@/lib/settings/cable-length-visibility";
import { useCableVdropVisibility } from "@/lib/settings/cable-vdrop-visibility";
import { useCableLabelVisibility } from "@/lib/settings/cable-label-visibility";
import { useCableHoverDetails } from "@/lib/settings/cable-hover-details";
import { formatLiveCurrent, formatPercent } from "@/lib/utils";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";
import { useCanvasForceMetricPills } from "./canvas-mode";
import { formatLength as formatLengthUnits, useUnits } from "@/lib/units";
import { FuseSymbol } from "@/components/ui/fuse-symbol";
import { fuseTypeDisplayName, resolveCableFuseType } from "@/lib/fuse-aic";
import { DEFAULT_NOMINAL_V, nominalClass } from "@/lib/voltage-domain";
import type { BlueprintCable } from "@/lib/blueprint";
import {
  polylineMidpoint,
  snapToParallelSegment,
  strokeWidth,
  type Pt,
} from "./cable-edge-geometry";
import {
  CableCommentChip,
  CableDetailsPill,
  CableHoverDetails,
  CableSizingValue,
  CableLabelTag,
} from "./cable-edge-parts";
import {
  bindCableTouchGesture,
} from "@/lib/touch-canvas-gesture";
import { bindPointerDrag } from "./pointer-drag";
import { forwardToNearestCableSegment, registerCableSegment } from "./cable-segment-hit-test";

export type CableEdgeData = {
  cable: Cable;
  /** Another cable is being dragged and would split this one on release. */
  cableTarget?: boolean;
  /** Worst-case current through this cable, path-walked at the canvas level. */
  demand?: { peak: number; continuous: number };
  /** Fully resolved by the shared system-sizing boundary. */
  spec: CableSpec;
  /** Signed live current; positive flows from the cable's `from` end to its `to` end. */
  bankCurrentA?: number;
  /** Main bus voltage; the pill prints a cable's own voltage only when it sits off it. */
  busNominalV?: number;
  /** Vertices reachable from a battery in simulate; undefined in edit mode. */
  simulateEnergized?: ReadonlySet<string>;
  nodes: SystemNode[];
  nodesById: Record<string, SystemNode>;
  cables: Cable[];
  /** Component geometry for the fallback route when waypoints are missing or stale. */
  previewObstacles?: RoutingScene;
  /** Print rendering: cable references only, no editor-only interaction. */
  blueprint?: boolean;
  blueprintCable?: BlueprintCable;
};

// Stable identities so an absent-edge-data render doesn't bust the memo deps.
const EMPTY_NODES: SystemNode[] = [];
const EMPTY_NODES_BY_ID: Record<string, SystemNode> = {};
const EMPTY_CABLES: Cable[] = [];

function CableEdgeInner({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
}: EdgeProps) {
  const tr = useT();
  const edgeData = data as CableEdgeData | undefined;
  const cable = edgeData?.cable;
  const cableTarget = edgeData?.cableTarget ?? false;
  const demand = edgeData?.demand;
  const bankCurrentA = edgeData?.bankCurrentA;
  const busNominalV = edgeData?.busNominalV ?? DEFAULT_NOMINAL_V;
  const simulateEnergized = edgeData?.simulateEnergized;
  const blueprint = edgeData?.blueprint ?? false;
  const blueprintCable = edgeData?.blueprintCable;
  const nodes = edgeData?.nodes ?? EMPTY_NODES;
  const nodesById = edgeData?.nodesById ?? EMPTY_NODES_BY_ID;
  const cables = edgeData?.cables ?? EMPTY_CABLES;
  const mode = useSystemStore((s) => s.mode);
  const system = useSystemStore((s) => s.system);
  const simulateComponentStates = useSystemStore(
    (s) => s.simulateState.componentStates,
  );
  const simulateLoadStates = useSystemStore(
    (s) => s.simulateState.loadStates,
  );
  const simulating = mode === "simulate" && !blueprint;
  const fusesVisible = useFuseVisibility((s) => s.visible);
  const cableSizeVisible = useCableSizeVisibility((s) => s.visible);
  const cableLengthVisible = useCableLengthVisibility((s) => s.visible);
  const cableVdropVisible = useCableVdropVisibility((s) => s.visible);
  const cableLabelVisible = useCableLabelVisibility((s) => s.visible);
  const cableHoverDetails = useCableHoverDetails((s) => s.visible);
  const units = useUnits((s) => s.units);
  const viewOnly = useCanvasViewOnly();
  const overviewProfile = useCanvasConnectionOverview();
  const hideCableLabels = useCanvasHideCableLabels();
  const connectionOverview = overviewProfile || isConnectionOverviewCable(cable);
  const isTouchLayout = useIsTouchLayout();
  const forceMetricPills = useCanvasForceMetricPills();
  const metricsHidden = connectionOverview || (isTouchLayout && !forceMetricPills);
  const commentCount = useCommentsStore((s) =>
    viewOnly ? commentsForCable(s.comments, id).length : 0,
  );
  const simulateCtx = useMemo(
    () =>
      simulating
        ? {
            componentStates: simulateComponentStates,
            loadStates: simulateLoadStates,
            energizedTerminals: simulateEnergized,
          }
        : undefined,
    [simulating, simulateComponentStates, simulateLoadStates, simulateEnergized],
  );
  const acAvailability = useMemo(() => simulateCtx
    ? computeAcAvailability(system, getDefinition, simulateCtx) : undefined,
  [system, simulateCtx]);
  const acStatus = cable && acAvailability ? acCableSupplyStatus(cable, acAvailability) : undefined;
  const [hovered, setHovered] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<Pt | null>(null);
  const [dragging, setDragging] = useState(false);
  const { getViewport, screenToFlowPosition, setViewport } = useReactFlow();

  const hoverNodeId = useCanvasHover((s) => s.hoverNodeId);
  const setHoverCableId = useCanvasHover((s) => s.setHoverCableId);
  const selectedNodeIds = useSystemStore((s) => s.selection.nodeIds);
  const focusedCableIds = useMemo(
    () => blueprint ? undefined : componentFocusCableIds(system, selectedNodeIds),
    [blueprint, system, selectedNodeIds],
  );
  const componentFocused = !!cable && (focusedCableIds?.has(cable.id) ||
    hoverNodeId === cable.fromNodeId || hoverNodeId === cable.toNodeId);

  // component-node reads this to show terminal labels while a cable is hovered.
  useEffect(() => {
    if (!hovered) return;
    setHoverCableId(id);
    return () => {
      if (useCanvasHover.getState().hoverCableId === id) setHoverCableId(null);
    };
  }, [hovered, id, setHoverCableId]);
  const dimmed = (() => {
    if (!cable) return false;
    if (blueprint) return false;
    if (selected || hovered) return false;
    if (selectedNodeIds.length === 0 && !hoverNodeId) return false;
    return !componentFocused;
  })();

  const cableRouter = useCableRouter();
  const draggedNodeIds = useCanvasDrag((s) => s.draggedNodeIds);
  const isGeometryDragging = useCanvasDrag((s) => s.isDragging);
  const sourceIsJunction = !!cable && isJunction(nodesById[cable.fromNodeId]);
  const targetIsJunction = !!cable && isJunction(nodesById[cable.toNodeId]);
  const movingEndpoint = useMemo(() => {
    if (!cable || draggedNodeIds.size === 0) return false;
    const underDragged = (nodeId: string): boolean => {
      let cur: SystemNode | undefined = nodesById[nodeId];
      while (cur) {
        if (draggedNodeIds.has(cur.id)) return true;
        cur = cur.parentId ? nodesById[cur.parentId] : undefined;
      }
      return false;
    };
    return underDragged(cable.fromNodeId) || underDragged(cable.toNodeId);
  }, [cable, draggedNodeIds, nodesById]);
  const previewObstacles = edgeData?.previewObstacles;
  // The router owns stored bends, endpoint repair and preview policy.
  const polyPoints = useMemo(() => cableRouter.route({
    source: { x: sourceX, y: sourceY },
    sourceSide: sourcePosition ?? Position.Right,
    target: { x: targetX, y: targetY },
    targetSide: targetPosition ?? Position.Left,
    cable,
    scene: previewObstacles,
    phase: isGeometryDragging ? "drag" : "idle",
    movingEndpoint,
    sourceIsJunction,
    targetIsJunction,
  }), [cableRouter, cable, previewObstacles, sourceX, sourceY, targetX, targetY,
    sourcePosition, targetPosition, isGeometryDragging, movingEndpoint,
    sourceIsJunction, targetIsJunction]);

  // Settle animation: morph to a new stored route instead of snapping to it.
  const SETTLE_SAMPLES = 24;
  const SETTLE_DURATION_MS = 180;
  const [animPoints, setAnimPoints] = useState<Pt[] | null>(null);
  const displayedRef = useRef<Pt[] | null>(null);
  const animRafRef = useRef<number | null>(null);
  const segmentDragRef = useRef(false);
  const segmentDragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => segmentDragCleanupRef.current?.(), []);
  const touchPanCleanupRef = useRef<(() => void) | null>(null);
  const suppressTouchClickUntilRef = useRef(0);
  useEffect(() => {
    const prev = displayedRef.current;
    const target = polyPoints;
    if (!prev || samePolyline(prev, target)) return;
    if (useCanvasDrag.getState().isDragging) return;
    if (segmentDragRef.current) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    const from = resamplePolyline(prev, SETTLE_SAMPLES);
    const to = resamplePolyline(target, SETTLE_SAMPLES);
    if (animRafRef.current != null) cancelAnimationFrame(animRafRef.current);
    const t0 = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / SETTLE_DURATION_MS);
      if (t >= 1) {
        setAnimPoints(null);
        animRafRef.current = null;
        return;
      }
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimPoints(lerpPolylines(from, to, eased));
      animRafRef.current = requestAnimationFrame(tick);
    };
    animRafRef.current = requestAnimationFrame(tick);
    // Only the stored-waypoint identity may trigger this — endpoint motion must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cable?.waypoints]);
  useEffect(() => {
    // Must stay after the settle effect, which reads the previous render's shape.
    displayedRef.current = animPoints ?? polyPoints;
  });
  useEffect(
    () => () => {
      if (animRafRef.current != null) cancelAnimationFrame(animRafRef.current);
      touchPanCleanupRef.current?.();
    },
    [],
  );

  const displayPoints = animPoints ?? polyPoints;
  const edgePath = animPoints
    ? polylinePath(animPoints)
    : roundedPath(polyPoints, 14);
  const labelPos = polylineMidpoint(displayPoints);

  const spec = edgeData?.spec ?? null;
  const baseSw = connectionOverview ? 2.4 : spec ? strokeWidth(spec.wire.mm2) : 2;
  const stroke = cable ? resolveCableColor(cable, nodesById, cables) : "var(--color-fg-muted)";

  // Signal wires have no current to size and no fuse to pick — hide those pills.
  const isSignalCable = (() => {
    if (!cable) return false;
    const aNode = nodesById[cable.fromNodeId];
    const bNode = nodesById[cable.toNodeId];
    const polOf = (n: typeof aNode, termId: string): string | null => {
      if (!n || n.kind !== "component") return null;
      const d = getDefinition(n.definitionId, n.customDefinition);
      return d?.terminals.find((t) => t.id === termId)?.polarity ?? null;
    };
    return (
      polOf(aNode, cable.fromTerminalId) === "signal" ||
      polOf(bNode, cable.toTerminalId) === "signal"
    );
  })();

  // Only modeled Vbatt+ leads have a signed result on the signal rail.
  const monitorSupply = simulating && isSignalCable && bankCurrentA != null;

  const resolvedCurrent = spec != null && !currentNeedsVerification(spec);
  const overload = resolvedCurrent && isOverloaded(spec);
  const highVDrop = resolvedCurrent && hasHighVDrop(spec);
  // Mirrors `detectUnderRatedUpstream`; the 5 % margin keeps rounding from tripping it.
  const cableFuseRating = cable ? getCableFuse(cable) : undefined;
  const fuseUnderRated =
    resolvedCurrent &&
    demand != null &&
    cableFuseRating != null &&
    sustainedCableCurrent(spec) > cableFuseRating * 1.05;
  // Ampacity is a continuous rating (ABYC/IEC tables), so surges don't count here.
  const wireUnderRated =
    resolvedCurrent &&
    demand != null &&
    demand.continuous > spec.wire.ampacity * 1.05;
  const underRated = fuseUnderRated || wireUnderRated;
  const warn = overload || highVDrop || underRated;
  const fuseRating = cableFuseRating;
  const renderedWarning = connectionOverview ? false : blueprint
    ? (blueprintCable?.warnings.length ?? 0) > 0 ||
      (blueprintCable?.diagnostics.length ?? 0) > 0
    : warn;

  // A signed solver result distinguishes a real milliamp draw from zero.
  // Retain the noise threshold on fallback estimates with no solved direction.
  const hasFlow = !spec?.isAc && (bankCurrentA != null ? Math.abs(bankCurrentA) >= 1e-6 : (spec?.continuous ?? 0) > 0.05);
  const direction = useMemo(() => {
    if (connectionOverview || !simulating || !cable || renderedWarning || !hasFlow) return "none";
    // Prefer the solved direction regardless of which end was drawn first.
    if (bankCurrentA != null) return bankCurrentA > 0 ? "forward" : "backward";
    return cableDirection(cable, nodes, cables, getDefinition, simulateCtx);
  }, [
    simulating,
    connectionOverview,
    cable,
    renderedWarning,
    hasFlow,
    nodes,
    cables,
    simulateCtx,
    bankCurrentA,
  ]);
  const flowing = direction !== "none";

  const flowDurationS = (() => {
    const amps = spec?.continuous ?? 0;
    if (amps <= 0.05) return 8;
    const ref = 10;
    const base = 1.6;
    const dur = base * Math.sqrt(ref / amps);
    return Math.min(8, Math.max(0.3, dur));
  })();

  const sw = baseSw + (selected ? 1.6 : hovered ? 1 : 0);
  const edgeStroke = selected
    ? "var(--color-accent)"
    : renderedWarning
      ? "var(--color-warning)"
      : stroke;
  const haloStroke = cableTarget
    ? "hsl(214 88% 52% / 0.32)"
    : selected
      ? "hsl(214 88% 52% / 0.22)"
      : hovered
        ? renderedWarning
          ? "hsl(28 90% 55% / 0.22)"
          : "hsl(30 8% 30% / 0.18)"
        : "transparent";
  const haloWidth = Math.max(sw + 10, 14);

  const handleCableTap = ({
    clientX,
    clientY,
    shiftKey,
    metaKey,
    ctrlKey,
  }: Pick<
    PointerEvent,
    "clientX" | "clientY" | "shiftKey" | "metaKey" | "ctrlKey"
  >) => {
    if (!cable) return;
    if (viewOnly) {
      if (isTouchLayout) {
        useSystemStore.getState().setSelection({
          nodeIds: [],
          cableIds: [cable.id],
        });
        return;
      }
      useCommentPopover.getState().openPopover(
        { kind: "cable", cableId: cable.id },
        new DOMRect(clientX, clientY, 1, 1),
      );
      return;
    }
    const additive = shiftKey || metaKey || ctrlKey;
    const current = useSystemStore.getState().selection;
    if (additive) {
      const nextIds = new Set(current.cableIds);
      if (nextIds.has(cable.id)) nextIds.delete(cable.id);
      else nextIds.add(cable.id);
      useSystemStore.getState().setSelection({
        nodeIds: current.nodeIds,
        cableIds: Array.from(nextIds),
      });
    } else {
      useSystemStore.getState().setSelection({
        nodeIds: [],
        cableIds: [cable.id],
      });
    }
  };

  /** Edge wrappers carry React Flow's `nopan` class. On touch, own the first
   * finger long enough to distinguish a tap from a viewport pan; a second
   * finger cancels this session before the canvas pinch arbiter runs. */
  const onCableTouchPointerDown = (
    event: React.PointerEvent<SVGLineElement>,
  ): boolean => {
    if (
      !isTouchLayout ||
      event.pointerType !== "touch" ||
      event.button !== 0 ||
      !cable
    ) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    touchPanCleanupRef.current?.();
    suppressTouchClickUntilRef.current = Date.now() + 750;

    const start = { x: event.clientX, y: event.clientY };
    const startViewport = getViewport();
    touchPanCleanupRef.current = bindCableTouchGesture({
      eventTarget: window,
      pointerId: event.pointerId,
      start,
      startViewport,
      onPan: (viewport) => {
        void setViewport(viewport, { duration: 0 });
      },
      onTap: (upEvent) => {
        suppressTouchClickUntilRef.current = Date.now() + 750;
        handleCableTap(upEvent);
      },
      onCancel: () => {
        suppressTouchClickUntilRef.current = Date.now() + 750;
      },
      onFinish: () => {
        // Refresh suppression after a slow pan; the pointerdown timestamp may
        // otherwise expire before the browser emits its compatibility click.
        suppressTouchClickUntilRef.current = Date.now() + 750;
        touchPanCleanupRef.current = null;
      },
    });
    return true;
  };

  /** Perpendicular segment drag; an end segment first gets a waypoint at the
   *  terminal so it can slide without dragging the terminal along. */
  const onSegmentPointerDown = (
    segIndex: number,
    orientation: "h" | "v",
    e: React.PointerEvent<SVGElement>,
    captureTarget: Element = e.currentTarget,
  ) => {
    if (!cable || e.button !== 0) return;
    if (viewOnly) return;
    // A captured segment drag wins over the canvas's TouchEvent pinch
    // arbiter and can rewrite waypoints while two fingers are zooming. Mobile
    // keeps tap-to-select plus the inspector's explicit Re-route action;
    // precision segment shaping remains a desktop gesture.
    if (isTouchLayout) return;
    e.stopPropagation();
    segmentDragCleanupRef.current?.();
    const cableId = cable.id;
    const startScreen = { x: e.clientX, y: e.clientY };
    const startFlow = screenToFlowPosition(startScreen, { snapToGrid: false });
    const startSource: Pt = { x: sourceX, y: sourceY };
    const startTarget: Pt = { x: targetX, y: targetY };
    const startPolyPoints = polyPoints;

    let aIdx = -1;
    let bIdx = -1;
    let startA: Pt = { x: 0, y: 0 };
    let startB: Pt = { x: 0, y: 0 };

    const beginDrag = () => {
      useSystemStore.getState().pushHistory();
      useCanvasDrag.getState().setIsDragging(true);
      setDragging(true);
      setHoverPoint(null);
      // Suppress the settle animation — every pointermove rewrites the waypoints.
      segmentDragRef.current = true;
      if (animRafRef.current != null) cancelAnimationFrame(animRafRef.current);
      setAnimPoints(null);
      document.body.style.cursor = orientation === "h" ? "ns-resize" : "ew-resize";
      const base: Pt[] = startPolyPoints.slice(1, -1).map((p) => ({ ...p }));
      let aWp = segIndex - 1; // -1 ⇒ source terminal
      let bWp = segIndex; //   === base.length ⇒ target terminal
      if (aWp === -1) {
        base.unshift({ ...startSource });
        aWp = 0;
        bWp += 1;
      }
      if (bWp === base.length) {
        base.push({ ...startTarget });
      }
      aIdx = aWp;
      bIdx = bWp;
      startA = { ...base[aIdx] };
      startB = { ...base[bIdx] };
      useSystemStore.getState().updateCable(cableId, { waypoints: base });
    };

    const apply = (dx: number, dy: number) => {
      const current = useSystemStore.getState().system.cables.find((c) => c.id === cableId);
      if (!current) return;
      const next = [...(current.waypoints ?? [])];
      if (aIdx < 0 || bIdx < 0 || aIdx >= next.length || bIdx >= next.length) return;
      // `next` holds the endpoints only for an end segment, so rebuild the full polyline.
      const ownPoly: Pt[] = [
        { x: sourceX, y: sourceY },
        ...next,
        { x: targetX, y: targetY },
      ];
      const ownASeg = aIdx + 1;
      if (orientation === "h") {
        const aStart = Math.min(startA.x, startB.x);
        const aEnd = Math.max(startA.x, startB.x);
        const raw = startA.y + dy;
        const ownSnap = findOwnParallelSnap(ownPoly, ownASeg, "h", aStart, aEnd, raw);
        const newY =
          ownSnap !== raw
            ? ownSnap
            : snapToParallelSegment(cableId, "h", aStart, aEnd, raw);
        next[aIdx] = { x: startA.x, y: newY };
        next[bIdx] = { x: startB.x, y: newY };
      } else {
        const aStart = Math.min(startA.y, startB.y);
        const aEnd = Math.max(startA.y, startB.y);
        const raw = startA.x + dx;
        const ownSnap = findOwnParallelSnap(ownPoly, ownASeg, "v", aStart, aEnd, raw);
        const newX =
          ownSnap !== raw
            ? ownSnap
            : snapToParallelSegment(cableId, "v", aStart, aEnd, raw);
        next[aIdx] = { x: newX, y: startA.y };
        next[bIdx] = { x: newX, y: startB.y };
      }
      useSystemStore.getState().updateCableWaypoints(cableId, next);
    };

    const onMove = (ev: PointerEvent) => {
      const point = screenToFlowPosition(
        { x: ev.clientX, y: ev.clientY },
        { snapToGrid: false },
      );
      apply(point.x - startFlow.x, point.y - startFlow.y);
    };

    const finish = (
      ev: PointerEvent | null,
      { started, cancelled }: { started: boolean; cancelled: boolean },
    ) => {
      segmentDragCleanupRef.current = null;
      document.body.style.cursor = "";
      if (!started) {
        if (cancelled || !ev) return;
        const additive = ev.shiftKey || ev.metaKey || ev.ctrlKey;
        const current = useSystemStore.getState().selection;
        if (additive) {
          const nextIds = new Set(current.cableIds);
          if (nextIds.has(cableId)) nextIds.delete(cableId);
          else nextIds.add(cableId);
          useSystemStore.getState().setSelection({
            nodeIds: current.nodeIds,
            cableIds: Array.from(nextIds),
          });
        } else {
          useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
        }
        return;
      }
      setDragging(false);
      segmentDragRef.current = false;
      useCanvasDrag.getState().setIsDragging(false);
      const current = useSystemStore.getState().system.cables.find((c) => c.id === cableId);
      if (!current) return;
      const poly = [
        { x: sourceX, y: sourceY },
        ...(current.waypoints ?? []),
        { x: targetX, y: targetY },
      ];
      const simplified = simplifyOrthogonal(poly).slice(1, -1);
      // Hand edits are transient: the router rewrites these on the next system change.
      useSystemStore
        .getState()
        .updateCableWaypoints(cableId, simplified.length ? simplified : undefined);
      // Jitter past the 3 px threshold otherwise leaves the cable unselected.
      useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
    };

    segmentDragCleanupRef.current = bindPointerDrag({
      event: e,
      // Segment simplification can remove the grabbed line during a drag.
      // Its owning group survives those geometry changes and keeps capture.
      target: captureTarget.parentElement ?? captureTarget,
      onStart: beginDrag,
      onMove,
      onFinish: finish,
    });
  };

  const segments = polyPoints.slice(0, -1).map((a, i) => {
    const b = polyPoints[i + 1];
    // Junction-fan segments are diagonal, so perpendicular drag doesn't apply.
    const isFan =
      (i === 0 && sourceIsJunction) ||
      (i === polyPoints.length - 2 && targetIsJunction);
    return { a, b, orientation: segmentOrientation(a, b), isFan };
  });

  const onSegmentPress = (i: number, e: React.PointerEvent<SVGLineElement>, target: SVGLineElement) => {
    const seg = segments[i];
    if (onCableTouchPointerDown(e)) return;
    if (seg.isFan) {
      if (!cable || e.button !== 0) return;
      // Bubble to React Flow's `onEdgeClick`, which opens the comment popover.
      if (viewOnly || isTouchLayout) return;
      e.stopPropagation();
      const pointerId = e.pointerId;
      const startScreen = { x: e.clientX, y: e.clientY };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        if (
          Math.hypot(
            ev.clientX - startScreen.x,
            ev.clientY - startScreen.y,
          ) >= (isTouchLayout ? 8 : 3)
        )
          return;
        const additive = ev.shiftKey || ev.metaKey || ev.ctrlKey;
        const current = useSystemStore.getState().selection;
        if (additive) {
          const next = new Set(current.cableIds);
          if (next.has(cable.id)) next.delete(cable.id);
          else next.add(cable.id);
          useSystemStore.getState().setSelection({
            nodeIds: current.nodeIds,
            cableIds: Array.from(next),
          });
        } else {
          useSystemStore.getState().setSelection({
            nodeIds: [],
            cableIds: [cable.id],
          });
        }
      };
      const onCancel = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
      };
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      return;
    }
    onSegmentPointerDown(i, seg.orientation, e, target);
  };

  const fuseTypeResolved = cable
    ? resolveCableFuseType(cable, nodes, getDefinition)
    : undefined;
  // The current solver reads each endpoint alone, so a cable touching an off engine
  // still reports the other end's amps — ~0 A doesn't catch it on its own.
  const endpointIsOff = (nodeId: string | undefined): boolean => {
    if (!nodeId) return false;
    const n = nodesById[nodeId];
    if (!n || n.kind !== "component") return false;
    const def = getDefinition(n.definitionId, n.customDefinition);
    if (!def) return false;
    return (
      getEffectiveStateOption(def, nodeId, {
        componentStates: simulateComponentStates,
        loadStates: simulateLoadStates,
      })?.ampOverride === 0
    );
  };
  const simulateInactive =
    simulating &&
    !isTouchLayout &&
    !selected &&
    !hovered &&
    (spec?.isAc ? acStatus === "off" :
      !hasFlow ||
        endpointIsOff(cable?.fromNodeId) ||
        endpointIsOff(cable?.toNodeId));
  // Selection keeps the whole physical circuit legible, including parallel
  // battery links and remote returns. Idle wires remain distinguishable without
  // fading the inspected circuit as far as unrelated wiring.
  const groupOpacity = dimmed ? 0.35 : simulateInactive ? (componentFocused ? 0.65 : 0.35) : 1;

  return (
    <g
      style={{
        opacity: groupOpacity,
        transition: "opacity 0.2s ease",
      }}
    >
      {/* Crossing jump: invisible on a lone cable, but at a crossing the
          later-painted cable's underlay punches a gap in the earlier one. */}
      <path
        d={edgePath}
        fill="none"
        stroke="var(--color-bg)"
        strokeWidth={sw + 4}
        strokeLinecap="butt"
        style={{ pointerEvents: "none" }}
      />
      <path
        d={edgePath}
        fill="none"
        stroke={haloStroke}
        strokeWidth={haloWidth}
        strokeLinecap="round"
        data-cable-id={cable?.id ?? id}
        style={{
          pointerEvents: "none",
          transition: "stroke 0.12s ease, stroke-width 0.12s ease",
        }}
      />
      {edgeStroke === "#ffffff" && <path d={edgePath} fill="none"
        stroke="var(--color-fg-muted)" strokeWidth={sw + 1.5}
        strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: "none" }} />}
      <BaseEdge
        id={id}
        path={edgePath}
        interactionWidth={0}
        data-flow-animated={flowing && !selected ? "true" : undefined}
        style={{
          stroke: edgeStroke,
          strokeWidth: sw,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeDasharray:
            renderedWarning && !selected
              ? `${sw * 3} ${sw * 2}`
              : flowing && !selected
                ? "10 6"
                : undefined,
          animationName: flowing && !selected ? "cable-flow" : undefined,
          animationDuration: flowing && !selected ? `${flowDurationS}s` : undefined,
          animationTimingFunction: flowing && !selected ? "linear" : undefined,
          animationIterationCount: flowing && !selected ? "infinite" : undefined,
          animationDirection:
            flowing && !selected && direction === "backward" ? "reverse" : undefined,
          fill: "none",
          cursor: "pointer",
          pointerEvents: "none",
          transition: "stroke 0.15s ease, stroke-width 0.12s ease",
        }}
      />
      {sw >= 2.6 && !renderedWarning && !selected && !flowing && (
        <path
          d={edgePath}
          fill="none"
          stroke="color-mix(in oklab, var(--color-surface) 28%, transparent)"
          strokeWidth={Math.max(0.6, sw - 2)}
          strokeLinecap="round"
          style={{ pointerEvents: "none" }}
        />
      )}
      {/* Transparent hit strips: hover, segment drag, and click-to-select. */}
      {!blueprint && segments.map((seg, i) => (
        <line
          key={`seg-${i}`}
          data-cable-segment=""
          ref={target => target ? registerCableSegment(target, (event, ownTarget) => onSegmentPress(i, event, ownTarget)) : undefined}
          x1={seg.a.x}
          y1={seg.a.y}
          x2={seg.b.x}
          y2={seg.b.y}
          stroke="transparent"
          strokeWidth={isTouchLayout ? 40 : Math.max(22, sw + 16)}
          vectorEffect="non-scaling-stroke"
          strokeLinecap="butt"
          style={{
            cursor: "pointer",
            pointerEvents: "stroke",
          }}
          onMouseEnter={(e) => {
            setHovered(true);
            setHoverPoint({ x: e.clientX, y: e.clientY });
          }}
          onMouseMove={(e) => {
            if (cableHoverDetails && !isTouchLayout && !dragging) setHoverPoint({ x: e.clientX, y: e.clientY });
          }}
          onMouseLeave={() => setHovered(false)}
          onClick={(event) => {
            // Desktop presses already select on release. A later click would
            // select the hit strip's owner again, undoing nearest-wire selection.
            if ((viewOnly || isTouchLayout) && Date.now() > suppressTouchClickUntilRef.current) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(e) => {
            if (!viewOnly && !isTouchLayout && forwardToNearestCableSegment(e,
              screenToFlowPosition({ x: e.clientX, y: e.clientY }, { snapToGrid: false }), getViewport().zoom)) return;
            onSegmentPress(i, e, e.currentTarget);
          }}
        />
      ))}

      {blueprint && !hideCableLabels && cable && blueprintCable?.reference && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelPos.x}px, ${labelPos.y}px)`,
              pointerEvents: "none",
            }}
            className="nodrag nopan"
          >
            <CableLabelTag
              text={blueprintCable.reference}
              color={cable.labelColor}
              selected={false}
              hovered={false}
              onClick={(event) => event.stopPropagation()}
              onHoverChange={() => undefined}
            />
          </div>
        </EdgeLabelRenderer>
      )}

      {!blueprint && spec && cable && !dimmed && (() => {
        const fromN = nodesById[cable.fromNodeId];
        const toN = nodesById[cable.toNodeId];
        // Suppresses the midpoint pill: a fusebox body draws the rating at the stud.
        const fuseOnBar =
          (fromN?.kind === "component" && fromN.isFusebox &&
            getDefinition(fromN.definitionId, fromN.customDefinition)
              ?.renderVariant === "busbar") ||
          (toN?.kind === "component" && toN.isFusebox &&
            getDefinition(toN.definitionId, toN.customDefinition)
              ?.renderVariant === "busbar");
        const cableLabel = cable.label?.trim() ?? "";
        const pills = resolveCableMetricPills({
          hovered: hovered && !dragging,
          selected: !!selected && !dragging,
          detailsOnInteraction: cableHoverDetails,
          metricsHidden,
          labelsHidden: hideCableLabels,
          isSignalCable: isSignalCable && !monitorSupply,
          simulating,
          cableSizeVisible,
          fusesVisible,
          cableLengthVisible,
          cableVdropVisible,
          cableLabelVisible: connectionOverview || cableLabelVisible,
          // An AC run is the inlet breaker's; a rating an old connect
          // flow left on it is not a fuse to draw.
          fuseRating: spec.isAc ? null : fuseRating ?? null,
          fuseOnBar: fuseOnBar ?? false,
          cableLabel,
          commentCount,
        });
        if (!pills.any) return null;
        const showCableSize = pills.size;
        const showFuse = pills.fuse;
        const showLength = pills.length;
        const showVdrop = pills.vdrop;
        const showLabel = pills.label;
        const pinnedLabel = connectionOverview || cableLabelVisible;
        const hoverDetails = hovered && cableHoverDetails && !isTouchLayout && !dragging && hoverPoint != null;
        const onPillClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (viewOnly) {
            const rect = new DOMRect(e.clientX, e.clientY, 1, 1);
            useCommentPopover.getState().openPopover(
              { kind: "cable", cableId: cable.id },
              rect,
            );
            return;
          }
          const additive = e.shiftKey || e.metaKey || e.ctrlKey;
          const current = useSystemStore.getState().selection;
          if (additive) {
            const next = new Set(current.cableIds);
            if (next.has(cable.id)) next.delete(cable.id);
            else next.add(cable.id);
            useSystemStore.getState().setSelection({
              nodeIds: current.nodeIds,
              cableIds: Array.from(next),
            });
          } else {
            useSystemStore.getState().setSelection({
              nodeIds: [],
              cableIds: [cable.id],
            });
          }
        };
        const pillBox = (
          <div
            data-cable-metrics={cable.id}
            className="flex items-center gap-2 rounded-[6px] px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums"
            style={{
              background: "var(--color-surface)",
              border: `1px solid ${selected ? "var(--color-accent)" : renderedWarning ? "var(--color-warning)" : hovered ? "var(--color-border-strong)" : "var(--color-border)"}`,
              color: renderedWarning ? "var(--color-warning)" : "var(--color-fg-muted)",
              boxShadow: selected || hovered ? "var(--shadow-sm)" : "none",
              whiteSpace: "nowrap",
              flexWrap: hoverDetails ? "wrap" : undefined,
              maxWidth: "100%",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              pointerEvents: "none",
            }}
          >
            {simulating && spec.isAc && acStatus && <span title={tr("simulate.ac.help")}>
              {tr(`simulate.ac.${acStatus}`)}
            </span>}
            {showCableSize && (
              simulating ? (
                <span style={{ color: "var(--color-fg)" }}>
                  {spec.isAc ? `${tr("cable.ac_rating")}: ` : ""}
                  {spec.isAc && !(Number.isFinite(spec.current) && spec.current > 0)
                    ? tr("cable.ac_rating_missing") : formatLiveCurrent(spec.continuous, monitorSupply)}
                </span>
              ) : (
                <span
                  style={{
                    color: renderedWarning ? "var(--color-warning)" : "var(--color-fg)",
                  }}
                >
                  <CableSizingValue spec={spec} units={units} />
                </span>
              )
            )}
            {showFuse && (
              <span
                className="inline-flex items-center gap-1"
                style={{ color: "var(--color-fg)" }}
              >
                <FuseSymbol />
                <span>{fuseRating} A{autoFuseNeedsReview(cable, spec) && <> · {tr("cable.needs_review")}</>}</span>
                {fuseTypeResolved && (
                  <span
                    style={{
                      color:
                        fuseTypeResolved.kind === "override"
                          ? "var(--color-fg)"
                          : "var(--color-fg-muted)",
                    }}
                  >
                    · {fuseTypeDisplayName(fuseTypeResolved.type)}
                  </span>
                )}
              </span>
            )}
            {showLength && (
              <span style={{ color: "var(--color-fg)" }}>
                {formatLengthUnits(cable.lengthM, units)}
              </span>
            )}
            {showVdrop && (
              <span
                title={spec.isAc ? tr("cable.ac_voltage_drop_unavailable") : undefined}
                style={{
                  color:
                    !spec.isAc && spec.vDropPct > 3
                      ? "var(--color-warning)"
                      : "var(--color-fg)",
                }}
              >
                {spec.isAc ? "—" : formatPercent(spec.vDropPct)}
              </span>
            )}
            {/* Compared by nominal band, printed as the real figure: an 18 V PV
                string reads 18 V, not the 24 V band it falls in. */}
            {!isSignalCable &&
              !metricsHidden &&
              nominalClass(spec.nominalV) !== nominalClass(busNominalV) && (
                <span style={{ color: "var(--color-fg-muted)" }}>
                  {simulating && spec.isAc ? tr("simulate.ac.nominal", { voltage: Math.round(spec.nominalV) }) : `${Math.round(spec.nominalV)} V`}
                </span>
              )}
          </div>
        );
        // z-index 1000 pops the pill above component nodes; the default stacking
        // leaves it above cables but behind those nodes.
        const popAbove = hovered || selected;
        const hasMetric = showCableSize || showFuse || showLength || showVdrop;
        const tagBox = showLabel ? (
          <CableLabelTag
            text={cableLabel}
            color={cable.labelColor}
            selected={!!selected}
            hovered={hovered}
            onClick={onPillClick}
            interactive={pinnedLabel}
            onHoverChange={(value) => {
              setHoverPoint(null);
              setHovered(value);
            }}
          />
        ) : null;
        return (
          <>
          <EdgeLabelRenderer>
            <div
              style={{
                position: "absolute",
                transform: `translate(-50%, -50%) translate(${labelPos.x}px, ${labelPos.y}px)`,
                pointerEvents: "none",
                zIndex: popAbove ? 1000 : undefined,
              }}
              className="nodrag nopan"
            >
              <div className="relative flex flex-col items-center gap-1">
                {commentCount > 0 && (
                  <CableCommentChip count={commentCount} cableId={id} />
                )}
                {(!hoverDetails || pinnedLabel) && tagBox}
                {!hoverDetails && hasMetric && pillBox}
              </div>
            </div>
          </EdgeLabelRenderer>
          {hoverDetails && (hasMetric || (!pinnedLabel && tagBox)) && <CableHoverDetails point={hoverPoint}>
            {!pinnedLabel && tagBox}
            {hasMetric && pillBox}
          </CableHoverDetails>}
          </>
        );
      })()}
      {/* Touch-only: the midpoint metric pill is suppressed there, leaving no
          other anchor for the spec sheet. */}
      {!blueprint && isTouchLayout && selected && cable && (
        <EdgeToolbar
          edgeId={id}
          x={labelPos.x}
          y={labelPos.y}
          isVisible
          alignY="top"
          className="nodrag nopan"
          style={{ zIndex: 1000 }}
        >
          <div style={{ transform: "translateY(4px)" }}>
            <CableDetailsPill cableId={id} />
          </div>
        </EdgeToolbar>
      )}
    </g>
  );
}

export const CableEdgeView = memo(CableEdgeInner);
